import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import {
  useDeleteLabReports,
  useGetLabReportDetail,
} from 'services/interpret-lab-report/hooks';
import Loader from 'components/Loader';
import useAuthStore from 'store/auth';
import ReportDetailInfo from 'components/reports/ReportDetailInfo';

enum TABS {
  INSIGHTS = 'insights',
  ACTUAL_REPORT = 'actual_report',
  INPUT_DATA = 'input_data',
}

interface ReportDetailProps {
  reportTokenId: string;
}

export default function ReportDetail({ reportTokenId }: ReportDetailProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const tabs = [
    { label: t('insights'), value: TABS.INSIGHTS },
    {
      label: t('actual_data'),
      value: TABS.ACTUAL_REPORT,
    },
    {
      label: t('input_data'),
      value: TABS.INPUT_DATA,
    },
  ];

  const [selectedTab, setSelectedTab] = useState<TABS>(TABS.INSIGHTS);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const {
    data: reportDetail,
    isFetching,
    isError,
  } = useGetLabReportDetail({
    tokenId: reportTokenId,
  });

  const { mutate: deleteReports } = useDeleteLabReports({
    onMutate: () => setIsDeleting(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['inifinite-lab-report', profileId],
      });
      setIsDeleting(false);
      toast.success(t('report_deleted_msg'));
    },
    onError: () => setIsDeleting(false),
  });

  useEffect(() => {
    if (
      reportDetail?.[0]?.ai_insights &&
      Object.keys(reportDetail?.[0]?.ai_insights)?.length
    )
      setSelectedTab(TABS.INSIGHTS);
    else setSelectedTab(TABS.ACTUAL_REPORT);
  }, [reportTokenId, reportDetail]);

  const onDelete = () => deleteReports([reportTokenId]);

  if (isFetching) {
    return <Loader />;
  }

  return (
    <ReportDetailInfo
      isDeleting={isDeleting}
      isError={isError}
      onDelete={onDelete}
      reportDetail={reportDetail}
      tabs={tabs}
      reportType='lab'
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
    />
  );
}
